import {keepPreviousData, queryOptions} from '@tanstack/react-query';
import {playersV2API, PlayerScope} from './api';
import {PlayerBrowseState, PlayerReportType} from './types';
import {canonicalWyscoutAnalyticsFilters, WyscoutAnalyticsFilters, wyscoutAnalyticsAPI} from './wyscoutAnalyticsApi';

const browseKeyState = (scope: PlayerScope, state: PlayerBrowseState) => {
  const keyState: Partial<PlayerBrowseState> = {...state};
  if (scope === 'squad') delete keyState.watching;
  return keyState;
};

export const playerKeys = {
  all: (workspaceId: string) => ['v2', 'players', workspaceId] as const,
  browse: (workspaceId: string, scope: PlayerScope, state: PlayerBrowseState) =>
    ['v2', 'players', workspaceId, scope, browseKeyState(scope, state)] as const,
  detail: (workspaceId: string, id: string) =>
    ['v2', 'players', workspaceId, 'detail', id] as const,
  wyscoutImport: (workspaceId: string, id: string) =>
    ['v2', 'players', workspaceId, 'detail', id, 'wyscout-import'] as const,
  wyscoutClubRoster: (workspaceId: string, id: string) =>
    [...playerKeys.detail(workspaceId, id), 'wyscout-club-roster'] as const,
  wyscoutAnalytics: (workspaceId: string, id: string, filters: WyscoutAnalyticsFilters) =>
    [...playerKeys.detail(workspaceId, id), 'wyscout-analytics', filters] as const,
  reports: (workspaceId: string, id: string, type: PlayerReportType | undefined, page: number) =>
    [...playerKeys.detail(workspaceId, id), 'reports', type, page] as const,
  section: (workspaceId: string, id: string, section: string, page: number) =>
    ['v2', 'players', workspaceId, 'detail', id, section, page] as const,
  latestSquadSync: (workspaceId: string) =>
    [...playerKeys.all(workspaceId), 'squad-sync', 'latest'] as const,
  squadReview: (workspaceId: string) =>
    [...playerKeys.all(workspaceId), 'squad-review'] as const,
};

export const playerBrowseQuery = (
  workspaceId: string,
  scope: PlayerScope,
  state: PlayerBrowseState,
) => queryOptions({
  queryKey: playerKeys.browse(workspaceId, scope, state),
  queryFn: () => playersV2API.browse(scope, state, workspaceId),
  placeholderData: keepPreviousData,
});

export const latestSquadSyncQuery = (workspaceId: string) => queryOptions({
  queryKey: playerKeys.latestSquadSync(workspaceId),
  queryFn: () => playersV2API.latestSquadSync(workspaceId),
});

export const squadReviewQuery = (workspaceId: string) => queryOptions({
  queryKey: playerKeys.squadReview(workspaceId),
  queryFn: () => playersV2API.squadReview(workspaceId),
});

export const wyscoutAnalyticsQuery = (
  workspaceId: string,
  playerId: string,
  filters: WyscoutAnalyticsFilters,
) => {
  const canonicalFilters = canonicalWyscoutAnalyticsFilters(filters);
  if (canonicalFilters === null) throw new Error('Invalid analytics filters');
  return queryOptions({
    queryKey: playerKeys.wyscoutAnalytics(workspaceId, playerId, canonicalFilters),
    queryFn: ({signal}) => wyscoutAnalyticsAPI.get(workspaceId, playerId, canonicalFilters, signal),
    placeholderData: keepPreviousData,
    retry: false,
    refetchInterval: query => query.state.data
      && (query.state.data.sync.status === 'queued' || query.state.data.sync.status === 'running') ? 5000 : false,
  });
};
